import { Duration } from "luxon";

import { roundNumber } from "./round-number";

const nowMillis = (): number => globalThis.performance?.now?.() ?? Date.now();

/**
 * Roughly modeled from the Stopwatch implementation in the .Net API
 */
class Stopwatch {
  private readonly name: string;
  private startTime: number | undefined;
  private endTime: number | undefined;

  private static stopwatchId = 0;

  public static startNew(name = "unnamed"): Stopwatch {
    const stopwatch = new Stopwatch(name);
    stopwatch.start();
    return stopwatch;
  }

  constructor(name?: string) {
    this.name = name ?? `Stopwatch #${Stopwatch.stopwatchId++}`;
  }

  public start() {
    this.startTime = nowMillis();
    this.endTime = undefined;
  }

  public stop() {
    this.endTime = nowMillis();
  }

  public isRunning() {
    return this.endTime === undefined;
  }

  public elapsed(): Duration {
    if (this.startTime === undefined) {
      return Duration.fromMillis(0);
    }
    const currentEndTime = this.isRunning() || !this.endTime ? nowMillis() : this.endTime;

    return Duration.fromMillis(currentEndTime - this.startTime);
  }

  public toString() {
    const elapsedMillis = this.roundedElapsedMillis();

    const elapsed =
      !this.isRunning() && (this.startTime === undefined || this.endTime === undefined)
        ? ""
        : `${elapsedMillis}ms `;

    const status = this.isRunning() ? "running" : "idle";

    return `[${this.name}] ${elapsed}(${status})`;
  }

  public toJSON() {
    return {
      name: this.name,
      elapsedMillis: this.roundedElapsedMillis(),
    };
  }

  private roundedElapsedMillis(): number {
    return roundNumber(this.elapsed().milliseconds, 3);
  }
}

export default Stopwatch;
